import { getConfig } from '@superbet-group/web.lib.config';
import { SuperAdvantageEligibility } from '@superbet-group/web.types';

import { transformMatchResults } from '@core/mappers/offer/transformMatchResults';
import { transformMetaData } from '@core/mappers/offer/transformMetaData';
import { transformOdd } from '@core/mappers/offer/transformOdd';
import { transformRule } from '@core/mappers/offer/transformRule';
import { OfferEventDto } from '@core/mappers/offer/dtos/OfferEventDto';
import OfferEvent, { getNameParts } from '@core/models/offer/OfferEvent';
import {
    OfferStatus,
    OfferType,
} from '@core/models/offer/enums';
import { OfferEventCountType } from '@core/services/offer/_private/models/enums';
import { toDate } from '@superbet-group/web.lib.utilities';

const excludedStreamTournaments = getConfig().countrySpecific.sport.excludedStreamTournaments || [];

const activeOfferStatuses = [OfferStatus.active, OfferStatus.block];

export function transformOfferEvent(dto: OfferEventDto): OfferEvent {
    const e: OfferEvent = new OfferEvent();
    const isLive = activeOfferStatuses.includes(dto.offerStateStatus?.[OfferType.live]);
    e.id = dto.eventId;
    e.uuid = dto.uuid;
    e.offerId = dto.offerId;

    const fullName = dto.matchName;
    e.fullName = fullName;
    const [team1Name, team2Name] = getNameParts(fullName);
    e.team1Name = team1Name;
    e.team2Name = team2Name;

    e.sportId = dto.sportId;
    e.tournamentId = dto.tournamentId;
    e.date = toDate(dto.utcDate);
    e.utcDate = dto.utcDate; // forwarded when saving ticket, to save on string -> Date -> string conversion
    e.oddCount = isLive ? dto.counts.odds[OfferEventCountType.live] : dto.counts.odds[OfferEventCountType.prematch];
    e.marketCount = isLive ? dto.counts.markets[OfferEventCountType.live] : dto.counts.markets[OfferEventCountType.prematch];
    e.team1Id = parseInt(dto.homeTeamId, 10);
    e.team2Id = parseInt(dto.awayTeamId, 10);
    e.code = dto.matchCode;
    e.incrementId = dto.incrementId;
    e.betRadarId = dto.betradarId;
    e.categoryId = dto.categoryId;
    e.offerStatus = dto.offerStateStatus;
    e.isGoingToBeInLiveBetting = dto.hasLive;
    e.rules = dto.resolvingRules ? dto.resolvingRules.map(transformRule) : [];
    e.metaData = dto.metadata ? transformMetaData(dto.metadata) : undefined;
    if (dto.matchFooter) {
        e.matchFooter = dto.matchFooter;
    }
    let odds: any[] = [];
    if (dto.odds) {
        odds = isLive
            ? dto.odds.filter((o) => o.offerStateId === OfferType.live)
            : dto.odds.filter((o) => o.offerStateId === OfferType.preEvent);
    }
    e.setOdds(odds.map(transformOdd));

    e.setOddResults(dto.oddsResults ? dto.oddsResults.map(transformOdd) : []);

    e.matchResults = transformMatchResults({
        matchResults: dto.matchResults,
        metaData: dto.metadata,
    });
    if (getConfig().countrySpecific.sport.isStreamAvailable && !excludedStreamTournaments.includes(e.tournamentId)) {
        e.hasStream = !!dto.hasStream;
    }
    e.streamInfo = dto.hasStream || {};
    e.matchTags = dto.matchTags ? dto.matchTags.split(',') : [];
    e.eventCategory = dto.eventCategory;
    e.matchTimestamp = dto.matchTimestamp;
    e.matchPromotedLiveOrder = dto.matchPromotedLiveOrder;
    e.superAdvantageEligibility = dto.superAdvantage as SuperAdvantageEligibility;
    e.tournamentPromotedLiveOrder = dto.tournamentPromotedLiveOrder;

    return e;
}
